import React, { useState } from 'react';
import { Cherry, Lock, Gift } from 'lucide-react';
import jsPDF from 'jspdf';

const API_BASE_URL = 'https://あなたのHerokuURL.herokuapp.com';
const STRIPE_PUBLIC_KEY = 'pk_test_51RxZIUF1YGk...';

const FourPillarsEngine = {
  heavenlyStems: ['甲', '乙', '丙', '丁', '戊', '己', '庚', '辛', '壬', '癸'],
  earthlyBranches: ['子', '丑', '寅', '卯', '辰', '巳', '午', '未', '申', '酉', '戌', '亥'],
  fiveElements: {
    '甲': '木', '乙': '木', '丙': '火', '丁': '火', '戊': '土',
    '己': '土', '庚': '金', '辛': '金', '壬': '水', '癸': '水',
    '子': '水', '丑': '土', '寅': '木', '卯': '木', '辰': '土',
    '巳': '火', '午': '火', '未': '土', '申': '金', '酉': '金',
    '戌': '土', '亥': '水'
  },

  calculateYearPillar(year) {
    const stemIndex = (year - 4) % 10;
    const branchIndex = (year - 4) % 12;
    return { stem: this.heavenlyStems[stemIndex], branch: this.earthlyBranches[branchIndex] };
  },

  calculateMonthPillar(year, month) {
    const yearStem = (year - 4) % 10;
    const monthStemBase = (yearStem % 5) * 2;
    const stemIndex = (monthStemBase + month - 1) % 10;
    const branchIndex = (month + 1) % 12;
    return { stem: this.heavenlyStems[stemIndex], branch: this.earthlyBranches[branchIndex] };
  },

  calculateDayPillar(year, month, day) {
    const date = new Date(year, month - 1, day);
    const baseDate = new Date(1900, 0, 1);
    const diffDays = Math.floor((date - baseDate) / (1000 * 60 * 60 * 24));
    const stemIndex = (diffDays + 6) % 10;
    const branchIndex = (diffDays + 8) % 12;
    return { stem: this.heavenlyStems[stemIndex], branch: this.earthlyBranches[branchIndex] };
  },

  analyzeFiveElements(pillars) {
    const elements = { 木: 0, 火: 0, 土: 0, 金: 0, 水: 0 };
    [pillars.year, pillars.month, pillars.day].forEach(pillar => {
      elements[this.fiveElements[pillar.stem]]++;
      elements[this.fiveElements[pillar.branch]]++;
    });
    return elements;
  },

  analyze(birthDate) {
    const year = birthDate.getFullYear();
    const month = birthDate.getMonth() + 1;
    const day = birthDate.getDate();
    const yearPillar = this.calculateYearPillar(year);
    const monthPillar = this.calculateMonthPillar(year, month);
    const dayPillar = this.calculateDayPillar(year, month, day);
    const elements = this.analyzeFiveElements({ year: yearPillar, month: monthPillar, day: dayPillar });
    return { year: yearPillar, month: monthPillar, day: dayPillar, elements, dayMaster: dayPillar.stem };
  }
};

const AnimalFortuneEngine = {
  animals: [
    { name: 'チータ', emoji: '🐆', trait: '行動力に優れ、目標達成能力が高い' },
    { name: 'ライオン', emoji: '🦁', trait: '統率力があり、人を惹きつける魅力' },
    { name: 'ゾウ', emoji: '🐘', trait: '慎重で思慮深く、信頼される存在' },
    { name: 'サル', emoji: '🐵', trait: '器用で好奇心旺盛、適応力がある' },
    { name: 'コアラ', emoji: '🐨', trait: 'マイペースで癒し系、独自の世界観' },
    { name: 'トラ', emoji: '🐯', trait: '情熱的で行動的、勇気がある' },
    { name: 'タヌキ', emoji: '🦝', trait: '社交的で愛嬌があり、人に好かれる' },
    { name: 'ヒツジ', emoji: '🐑', trait: '優しく穏やかで、協調性がある' },
    { name: 'ペガサス', emoji: '🦄', trait: '自由で創造的、型にはまらない' },
    { name: 'オオカミ', emoji: '🐺', trait: '一匹狼タイプ、独立心が強い' },
    { name: 'こじか', emoji: '🦌', trait: '純粋で感受性豊か、繊細な心' },
    { name: '黒ヒョウ', emoji: '🐈‍⬛', trait: 'クールで神秘的、内に秘めた情熱' }
  ],
  calculate(birthDate) {
    const year = birthDate.getFullYear();
    const month = birthDate.getMonth() + 1;
    const day = birthDate.getDate();
    const index = ((year + month * 3 + day * 7) % 12);
    return this.animals[index];
  }
};

function App() {
  const [birthDate, setBirthDate] = useState('');
  const [result, setResult] = useState(null);
  const [couponCode, setCouponCode] = useState('');
  const [couponApplied, setCouponApplied] = useState(false);
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [showPayment, setShowPayment] = useState(false);

  const validCoupons = { 'UNMEI2025': true, 'WELCOME': true };

  const handleAnalyze = () => {
    if (!birthDate) return;
    setLoading(true);
    
    setTimeout(() => {
      const date = new Date(birthDate);
      const fourPillars = FourPillarsEngine.analyze(date);
      const animal = AnimalFortuneEngine.calculate(date);
      
      setResult({ fourPillars, animal });
      setLoading(false);
    }, 1000);
  };

  const handleCouponApply = () => {
    if (validCoupons[couponCode.toUpperCase()]) {
      setCouponApplied(true);
      alert('クーポン適用されました。完全版を御覧いただけます。');
    } else {
      alert('無効なクーポンでございます。');
    }
  };

  const handlePurchase = () => {
    if (!email) {
      alert('メールアドレスを入力してください');
      return;
    }
    setShowPayment(true);
  };

  const handlePaymentComplete = () => {
    setCouponApplied(true);
    setShowPayment(false);
    alert('お支払いありがとうございます！完全版をお楽しみください。');
  };

  const generatePDF = () => {
    const doc = new jsPDF();
    
    // 日本語フォント対応のため、テキストを画像化するか、英数字のみで作成
    // ここでは英数字ベースで作成
    
    // タイトル
    doc.setFontSize(22);
    doc.text('Lien占い鑑定書', 105, 20, { align: 'center' });
    
    doc.setFontSize(16);
    doc.text('Complete Fortune Reading Report', 105, 30, { align: 'center' });
    
    // 線
    doc.setLineWidth(0.5);
    doc.line(20, 35, 190, 35);
    
    // 生年月日
    doc.setFontSize(12);
    doc.text(`Birth Date: ${birthDate}`, 20, 45);
    
    // 四柱推命
    doc.setFontSize(14);
    doc.text('Four Pillars (Si-Zhu Ming-Li)', 20, 60);
    doc.setFontSize(11);
    doc.text(`Year Pillar: ${result.fourPillars.year.stem}${result.fourPillars.year.branch}`, 30, 70);
    doc.text(`Month Pillar: ${result.fourPillars.month.stem}${result.fourPillars.month.branch}`, 30, 78);
    doc.text(`Day Pillar: ${result.fourPillars.day.stem}${result.fourPillars.day.branch}`, 30, 86);
    doc.text(`Day Master: ${result.fourPillars.dayMaster}`, 30, 94);
    
    const element = FourPillarsEngine.fiveElements[result.fourPillars.dayMaster];
    doc.text(`Five Element: ${element}`, 30, 102);
    
    // 動物占い
    doc.setFontSize(14);
    doc.text('Animal Fortune', 20, 120);
    doc.setFontSize(11);
    doc.text(`Your Animal: ${result.animal.name}`, 30, 130);
    doc.text(`Trait: ${result.animal.trait}`, 30, 138);
    
    // 性格分析
    doc.setFontSize(14);
    doc.text('Personality Analysis', 20, 155);
    doc.setFontSize(10);
    
    const personalityText = `The combination of ${result.fourPillars.year.stem}${result.fourPillars.year.branch} (Year), ` +
      `${result.fourPillars.month.stem}${result.fourPillars.month.branch} (Month), and ` +
      `${result.fourPillars.day.stem}${result.fourPillars.day.branch} (Day) indicates a harmonious ` +
      `balance of the Five Elements, suggesting diverse talents and adaptability.`;
    
    const splitText = doc.splitTextToSize(personalityText, 170);
    doc.text(splitText, 20, 165);
    
    // 運勢
    doc.setFontSize(14);
    doc.text('Fortune for 2025', 20, 190);
    doc.setFontSize(10);
    doc.text('Career: Strong potential in creative and leadership roles.', 30, 200);
    doc.text('Love: Sincere and devoted relationship tendencies.', 30, 208);
    doc.text('Health: Maintain balance and seasonal awareness.', 30, 216);
    
    // ラッキーアイテム
    doc.setFontSize(14);
    doc.text('Lucky Items', 20, 235);
    doc.setFontSize(10);
    doc.text('Color: Blue, Green', 30, 245);
    doc.text('Direction: Southeast', 30, 252);
    doc.text('Numbers: 3, 8', 30, 259);
    doc.text('Item: Indoor Plants', 30, 266);
    
    // フッター
    doc.setFontSize(8);
    doc.text('Generated by unmei.tokyo', 105, 285, { align: 'center' });
    doc.text('For entertainment purposes only', 105, 290, { align: 'center' });
    
    // PDF保存
    const fileName = `unmei-tokyo-${birthDate.replace(/-/g, '')}.pdf`;
    doc.save(fileName);
    
    alert(`PDFをダウンロードしました: ${fileName}`);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 via-red-50 to-orange-50">
      <div className="fixed inset-0 opacity-10 pointer-events-none">
        <div className="absolute top-10 left-10 text-8xl">🌸</div>
        <div className="absolute top-40 right-20 text-6xl">🍂</div>
        <div className="absolute bottom-20 left-1/4 text-7xl">🌸</div>
        <div className="absolute bottom-40 right-1/3 text-5xl">🍁</div>
      </div>

      <header className="bg-gradient-to-r from-red-900 via-red-800 to-orange-900 text-white shadow-lg relative">
        <div className="absolute inset-0 bg-black opacity-20"></div>
        <div className="max-w-6xl mx-auto px-4 py-8 relative">
          <div className="text-center">
            <div className="flex items-center justify-center gap-4 mb-2">
              <Cherry className="w-8 h-8" />
              <h1 className="text-4xl font-bold" style={{ fontFamily: 'serif' }}>Lien占い</h1>
              <Cherry className="w-8 h-8" />
            </div>
            <p className="text-amber-200 text-sm" style={{ fontFamily: 'serif' }}>〜 運命の糸を紡ぐ 〜</p>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-12 relative">
        <div className="bg-white rounded-lg shadow-2xl p-8 mb-8 border-4 border-red-800" style={{ borderStyle: 'double' }}>
          <div className="text-center mb-6">
            <h2 className="text-2xl font-bold text-red-900 mb-2" style={{ fontFamily: 'serif' }}>御生年月日を御入力くださいませ</h2>
            <div className="w-32 h-1 bg-gradient-to-r from-transparent via-red-600 to-transparent mx-auto"></div>
          </div>
          
          <div className="space-y-6">
            <div className="max-w-md mx-auto">
              <label className="block text-sm mb-2 text-red-900 font-semibold" style={{ fontFamily: 'serif' }}>御生年月日</label>
              <input
                type="date"
                value={birthDate}
                onChange={(e) => setBirthDate(e.target.value)}
                className="w-full px-4 py-3 border-2 border-red-300 rounded-lg focus:outline-none focus:border-red-600 bg-amber-50"
                disabled={loading}
              />
            </div>

            {!couponApplied && result && (
              <div className="max-w-md mx-auto bg-gradient-to-r from-amber-100 to-orange-100 rounded-lg p-4 border-2 border-orange-400">
                <div className="flex items-center gap-2 mb-3 justify-center">
                  <Gift className="w-5 h-5 text-red-700" />
                  <span className="font-semibold text-red-900" style={{ fontFamily: 'serif' }}>御優待券をお持ちですか</span>
                </div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={couponCode}
                    onChange={(e) => setCouponCode(e.target.value)}
                    placeholder="御優待番号を入力"
                    className="flex-1 px-4 py-2 border-2 border-orange-300 rounded-lg bg-white"
                    disabled={loading}
                  />
                  <button
                    onClick={handleCouponApply}
                    disabled={loading}
                    className="px-6 py-2 bg-red-700 hover:bg-red-600 text-white rounded-lg font-semibold transition"
                  >
                    適用
                  </button>
                </div>
              </div>
            )}

            <div className="text-center">
              <button
                onClick={handleAnalyze}
                disabled={loading || !birthDate}
                className="px-12 py-4 bg-gradient-to-r from-red-800 to-orange-800 hover:from-red-700 hover:to-orange-700 text-white rounded-full font-bold text-lg shadow-xl transition transform hover:scale-105 disabled:opacity-50"
                style={{ fontFamily: 'serif' }}
              >
                {loading ? '鑑定中...' : '運命を紐解く'}
              </button>
            </div>
          </div>
        </div>

        {result && (
          <div className="space-y-8">
            <div className="bg-white rounded-lg shadow-2xl p-8 border-4 border-red-800" style={{ borderStyle: 'double' }}>
              <div className="text-center mb-8">
                <h3 className="text-3xl font-bold text-red-900 mb-2" style={{ fontFamily: 'serif' }}>御命式</h3>
                <div className="w-40 h-1 bg-gradient-to-r from-transparent via-red-600 to-transparent mx-auto"></div>
              </div>

              <div className="grid md:grid-cols-3 gap-6 mb-8">
                <div className="bg-gradient-to-br from-amber-100 to-orange-100 rounded-lg p-6 border-2 border-orange-400 text-center">
                  <div className="text-sm text-red-800 mb-2 font-semibold">年柱</div>
                  <div className="text-4xl font-bold text-red-900 mb-1">{result.fourPillars.year.stem}</div>
                  <div className="text-3xl font-bold text-orange-800">{result.fourPillars.year.branch}</div>
                  <div className="text-xs text-gray-600 mt-2">幼少期の環境</div>
                </div>

                <div className="bg-gradient-to-br from-red-100 to-pink-100 rounded-lg p-6 border-2 border-red-400 text-center">
                  <div className="text-sm text-red-800 mb-2 font-semibold">月柱</div>
                  <div className="text-4xl font-bold text-red-900 mb-1">{result.fourPillars.month.stem}</div>
                  <div className="text-3xl font-bold text-red-800">{result.fourPillars.month.branch}</div>
                  <div className="text-xs text-gray-600 mt-2">青年期・仕事運</div>
                </div>

                <div className="bg-gradient-to-br from-yellow-100 to-amber-100 rounded-lg p-6 border-2 border-yellow-500 text-center">
                  <div className="text-sm text-red-800 mb-2 font-semibold">日柱</div>
                  <div className="text-4xl font-bold text-red-900 mb-1">{result.fourPillars.day.stem}</div>
                  <div className="text-3xl font-bold text-amber-800">{result.fourPillars.day.branch}</div>
                  <div className="text-xs text-gray-600 mt-2">本質・結婚運</div>
                </div>
              </div>

              <div className="bg-gradient-to-r from-red-50 to-orange-50 rounded-lg p-6 mb-6 border-l-4 border-red-600">
                <div className="text-center mb-4">
                  <span className="text-sm text-red-800">日干（命式の核）</span>
                  <div className="text-5xl font-bold text-red-900 my-2">{result.fourPillars.dayMaster}</div>
                  <span className="text-lg text-orange-800">五行: {FourPillarsEngine.fiveElements[result.fourPillars.dayMaster]}</span>
                </div>
              </div>

              <div className="bg-gradient-to-br from-pink-50 to-red-50 rounded-lg p-6 border-2 border-pink-400">
                <h4 className="text-xl font-bold text-red-900 mb-4 text-center" style={{ fontFamily: 'serif' }}>御守護動物</h4>
                <div className="text-center">
                  <div className="text-6xl mb-3">{result.animal.emoji}</div>
                  <div className="text-2xl font-bold text-red-900 mb-2">{result.animal.name}</div>
                  <div className="text-sm text-gray-700">{result.animal.trait}</div>
                </div>
              </div>
            </div>

            {!couponApplied ? (
              <div className="bg-gradient-to-br from-yellow-50 to-amber-100 rounded-lg shadow-2xl p-8 border-4 border-yellow-600" style={{ borderStyle: 'double' }}>
                <div className="text-center mb-6">
                  <Lock className="w-16 h-16 mx-auto mb-4 text-yellow-700" />
                  <h3 className="text-2xl font-bold mb-2 text-red-900" style={{ fontFamily: 'serif' }}>完全版にて更なる開運を</h3>
                  <p className="text-gray-700" style={{ fontFamily: 'serif' }}>詳細なる性格分析、月運、相性診断など充実の内容</p>
                </div>
                
                <div className="bg-white rounded-lg p-6 mb-6 border-2 border-yellow-500">
                  <h4 className="font-bold mb-4 text-red-900" style={{ fontFamily: 'serif' }}>完全版の内容</h4>
                  <ul className="space-y-2 text-sm text-gray-700" style={{ fontFamily: 'serif' }}>
                    <li>◉ 四柱の詳細分析と運命の流れ</li>
                    <li>◉ 仕事運・恋愛運・健康運の詳細</li>
                    <li>◉ 月別運勢暦</li>
                    <li>◉ 開運法・吉方位</li>
                    <li>◉ 相性診断</li>
                    <li>◉ PDF鑑定書</li>
                  </ul>
                </div>

                <div className="mb-4">
                  <label className="block text-sm mb-2 text-red-900 font-semibold" style={{ fontFamily: 'serif' }}>
                    メールアドレス（領収書送付用）
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    className="w-full px-4 py-3 border-2 border-yellow-400 rounded-lg bg-white text-gray-800"
                  />
                </div>
                
                <button 
                  onClick={handlePurchase}
                  className="w-full py-4 bg-gradient-to-r from-red-700 to-orange-700 hover:from-red-600 hover:to-orange-600 text-white rounded-full font-bold text-lg shadow-xl transition transform hover:scale-105" 
                  style={{ fontFamily: 'serif' }}
                >
                  完全版を購入する（九百八十円）
                </button>
              </div>
            ) : (
              <div className="space-y-6">
                <div className="bg-white rounded-lg shadow-2xl p-8 border-4 border-yellow-600" style={{ borderStyle: 'double' }}>
                  <div className="text-center mb-6">
                    <div className="text-6xl mb-4">✨</div>
                    <h3 className="text-2xl font-bold text-red-900 mb-2" style={{ fontFamily: 'serif' }}>完全版鑑定書</h3>
                    <p className="text-sm text-gray-600">ご購入ありがとうございます</p>
                  </div>

                  <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-lg p-6 mb-6 border-l-4 border-red-600">
                    <h4 className="text-xl font-bold text-red-900 mb-4" style={{ fontFamily: 'serif' }}>詳細なる性格分析</h4>
                    <p className="text-gray-800 leading-relaxed" style={{ fontFamily: 'serif' }}>
                      四柱の構成から見ると、年柱の{result.fourPillars.year.stem}{result.fourPillars.year.branch}は幼少期の環境を示し、
                      月柱の{result.fourPillars.month.stem}{result.fourPillars.month.branch}は青年期と仕事運を、
                      日柱の{result.fourPillars.day.stem}{result.fourPillars.day.branch}は本質と結婚運を表しております。
                      五行の調和により、多彩なる才能をお持ちです。
                    </p>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6 mb-6">
                    <div className="bg-white rounded-lg shadow-lg p-6 border-2 border-blue-400">
                      <h4 className="text-lg font-bold text-red-900 mb-3" style={{ fontFamily: 'serif' }}>仕事運</h4>
                      <p className="text-gray-700 text-sm leading-relaxed" style={{ fontFamily: 'serif' }}>
                        五行のバランスから、創造性と実務能力を兼ね備えた職種が適しております。指導的立場にて力を発揮されましょう。新しい挑戦にも恵まれる年です。
                      </p>
                    </div>

                    <div className="bg-white rounded-lg shadow-lg p-6 border-2 border-pink-400">
                      <h4 className="text-lg font-bold text-red-900 mb-3" style={{ fontFamily: 'serif' }}>恋愛運</h4>
                      <p className="text-gray-700 text-sm leading-relaxed" style={{ fontFamily: 'serif' }}>
                        日柱より見る恋愛の傾向は、誠実にして一途なる御性格。相手の内面を重んじ、深き絆を結ぶ傾向にございます。
                      </p>
                    </div>

                    <div className="bg-white rounded-lg shadow-lg p-6 border-2 border-green-400">
                      <h4 className="text-lg font-bold text-red-900 mb-3" style={{ fontFamily: 'serif' }}>健康運</h4>
                      <p className="text-gray-700 text-sm leading-relaxed" style={{ fontFamily: 'serif' }}>
                        五行の均衡に留意し、特に季節の変わり目には御身体の管理を心がけられませ。適度なる運動と休息が肝要です。
                      </p>
                    </div>

                    <div className="bg-white rounded-lg shadow-lg p-6 border-2 border-purple-400">
                      <h4 className="text-lg font-bold text-red-900 mb-3" style={{ fontFamily: 'serif' }}>相性診断</h4>
                      <p className="text-gray-700 text-sm leading-relaxed" style={{ fontFamily: 'serif' }}>
                        水の気を持つ御方との相性良好。互いに高め合う縁を結べましょう。土の気の方とは安定した関係を築けます。
                      </p>
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg p-6 mb-6 border-2 border-green-500">
                    <h4 className="text-xl font-bold text-red-900 mb-6 text-center" style={{ fontFamily: 'serif' }}>開運の御導き</h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                      <div className="text-center">
                        <div className="text-xs text-gray-600 mb-2">吉色</div>
                        <div className="text-lg font-bold text-red-900">青・緑</div>
                      </div>
                      <div className="text-center">
                        <div className="text-xs text-gray-600 mb-2">吉方</div>
                        <div className="text-lg font-bold text-red-900">東南</div>
                      </div>
                      <div className="text-center">
                        <div className="text-xs text-gray-600 mb-2">吉数</div>
                        <div className="text-lg font-bold text-red-900">三、八</div>
                      </div>
                      <div className="text-center">
                        <div className="text-xs text-gray-600 mb-2">開運品</div>
                        <div className="text-sm font-bold text-red-900">観葉植物</div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg p-6 mb-6 border-2 border-purple-400">
                    <h4 className="text-xl font-bold text-red-900 mb-4 text-center" style={{ fontFamily: 'serif' }}>月別運勢</h4>
                    <div className="space-y-3">
                      {[
                        { month: '睦月（1月）', fortune: '★★★★☆', advice: '新春、好機到来' },
                        { month: '如月（2月）', fortune: '★★★☆☆', advice: '人との縁を大切に' },
                        { month: '弥生（3月）', fortune: '★★★★★', advice: '運気上昇、積極的に' },
                        { month: '卯月（4月）', fortune: '★★★★☆', advice: '新しい出会いの予感' },
                        { month: '皐月（5月）', fortune: '★★★☆☆', advice: '焦らず着実に' },
                        { month: '水無月（6月）', fortune: '★★★★☆', advice: '変化を楽しむ時期' }
                      ].map((item, i) => (
                        <div key={i} className="flex items-center justify-between bg-white rounded px-4 py-3">
                          <span className="font-semibold text-gray-800">{item.month}</span>
                          <span className="text-yellow-600">{item.fortune}</span>
                          <span className="text-sm text-gray-600">{item.advice}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <button 
                    onClick={generatePDF}
                    className="w-full py-4 bg-gradient-to-r from-green-700 to-emerald-700 hover:from-green-600 hover:to-emerald-600 text-white rounded-lg font-bold text-lg shadow-xl transition transform hover:scale-105 flex items-center justify-center gap-2"
                    style={{ fontFamily: 'serif' }}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    完全版鑑定書（PDF）をダウンロード
                  </button>

                  <p className="text-xs text-center text-gray-500 mt-4">
                    ※ PDFは{email}宛にも送信されます
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        {showPayment && (
          <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg p-8 max-w-md w-full border-4 border-red-700" style={{ borderStyle: 'double' }}>
              <h3 className="text-2xl font-bold mb-6 text-center text-red-900" style={{ fontFamily: 'serif' }}>御支払い</h3>
              
              <div className="bg-amber-50 rounded-lg p-6 mb-6 border-2 border-orange-400">
                <div className="flex justify-between mb-4">
                  <span>完全版鑑定書</span>
                  <span className="font-bold">¥980</span>
                </div>
                <div className="border-t-2 border-orange-300 pt-4">
                  <div className="flex justify-between text-xl font-bold text-red-900">
                    <span>合計</span>
                    <span>¥980</span>
                  </div>
                </div>
              </div>

              <div className="space-y-4 mb-6">
                <input type="text" placeholder="カード番号 (テスト: 4242 4242 4242 4242)" className="w-full px-4 py-3 border-2 border-red-300 rounded-lg" />
                <div className="grid grid-cols-2 gap-4">
                  <input type="text" placeholder="MM/YY" className="px-4 py-3 border-2 border-red-300 rounded-lg" />
                  <input type="text" placeholder="CVC" className="px-4 py-3 border-2 border-red-300 rounded-lg" />
                </div>
                <p className="text-xs text-gray-600 text-center">テストカード: 4242 4242 4242 4242 / 有効期限: 12/34 / CVC: 123</p>
              </div>

              <div className="flex gap-3">
                <button onClick={() => setShowPayment(false)} className="flex-1 py-3 bg-gray-400 hover:bg-gray-500 text-white rounded-lg font-semibold">戻る</button>
                <button onClick={handlePaymentComplete} className="flex-1 py-3 bg-gradient-to-r from-red-700 to-orange-700 hover:from-red-600 hover:to-orange-600 text-white rounded-lg font-bold">決済する</button>
              </div>
            </div>
          </div>
        )}
      </main>

      <footer className="bg-gradient-to-r from-red-900 to-orange-900 text-white mt-12 py-8">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <p className="text-sm" style={{ fontFamily: 'serif' }}>© 令和七年 unmei.tokyo 不許複製</p>
          <p className="text-xs mt-2 text-amber-200">鑑定結果は娯楽の為のものです</p>
        </div>
      </footer>
    </div>
  );
}

export default App;